import { DatePicker } from '@mui/lab';
import { Checkbox, FormControlLabel, Grid, useEventCallback } from '@mui/material';
import { FormikErrors, FormikTouched } from 'formik';
import { DateTime } from 'luxon';
import AcquisitionSystemList from 'practice/components/shared/acquisition-system-list';
import { Outcome } from 'practice/constants/outcome';
import { TypeDomainPractice } from 'practice/constants/type-practice-domain';
import { Fragment, memo, ReactElement, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { PmiButton } from 'shell/components/shared/button';
import Table from 'shell/components/shared/table';
import { BaseTableRows } from 'shell/components/shared/table/types';
import { PmiTextField } from 'shell/components/shared/text-field';
import { __PRACTICE_API_URL__ } from 'shell/constants/environment';
import { regexOnlyNumber } from 'shell/constants/regex';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { HttpResponse } from 'shell/models/http-response';
import { PracticeData } from 'shell/models/practice';
import { noop } from 'shell/utils/noop';
import { CirculationAreaClient } from 'shell/view-models/circulation-area-client';
import AddressAutoComplete from 'system/components/shared/search-address';
import StreetNumberList, { StreetNumberVM } from 'system/components/shared/street-number-list';
import { transformExponent } from 'system/components/shared/street-number-list/transform-exponent';
import * as yup from 'yup';
import { HeadCell } from 'shell/components/shared/table/types';
import { PmiTitleDivider } from 'shell/components/shared/pmi-title-divider';
import { ModalViewerPDF } from 'shell/components/shared/pdf-modal';
import { ModalPdfViewerState } from 'shell/view-models/pdf-modal';
import { blobToBase64 } from 'shell/utils/base64-to-blob';
import { toast } from 'react-toastify';
import ModalPrintSuccess from '../print-comunication/modal';
import { IconButton } from '@mui/material';
import { AddCircleOutline, Edit } from '@mui/icons-material';
import { Typography } from '@mui/material';
import ModalAddProprietario from './modal-add-proprietario';
import { TypeRegister } from 'practice/constants/type-register';

export const headCells: HeadCell<ProprietarioVM, keyof ProprietarioVM>[] = [
  {
    id: 'fiscalCode',
    label: 'CF',
  },
  {
    id: 'surname',
    label: 'Cognome',
  },
  {
    id: 'name',
    label: 'Nome',
  },
  {
    id: 'residence',
    label: 'Residenza',
  },
  {
    id: 'state',
    label: 'Stato Notifica',
  },
  {
    id: 'requireNotification',
    overlay: true,
    label: 'Richiesta Notifica',
  },
  {
    id: 'action',
    label: '',
  },
];

export const schema = yup.object().shape({
  date: yup.date().typeError('Formato non valido').nullable().required('Campo obbligatorio'),
  outcome: yup.string().nullable().required('Campo obbligatorio'),
  cap: yup
    .string()
    .nullable()
    .matches(regexOnlyNumber, 'Inserire numero codice avviamento postale'),
});

export interface ProprietarioVO {
  note: string;
  dataNascita: string;
  tipoProprietario: string;
  cognome: string;
  indirizzo: string;
  provincia: string;
  progressivoPratica: number;
  cap: string;
  testoNote: string;
  tipoRegistro: string;
  codiceEnte: string;
  flagRichiestaNotifica: '1' | null;
  idAnnoPratica: number;
  statoNotifica: string;
  key: {
    idProprietario: number;
  };
  codFiscale: string;
  nome: string;
  comune: string;
}

interface ProprietariResponse {
  proprietari?: Array<ProprietarioVO>;
  struttura?: boolean;
}

export interface ProprietarioVM extends BaseTableRows {
  id: string;
  fiscalCode?: string;
  surname?: string;
  name?: string;
  residence?: string;
  state?: string;
  disabled?: boolean;
  checked?: boolean;
  requireNotification?: ReactElement;
  proprietarioBE?: ProprietarioVO;
  action?: React.ReactElement;
}

interface ProprietariRes {
  list?: Array<ProprietarioVM>;
  struttura?: boolean;
}

export interface VerificationMessiVM {
  date?: DateTime | null;
  outcome?: string;
  number?: number;
  exponent?: string;
  note?: string;
  noteMesso?: string;
  addressAuto?: CirculationAreaClient | null;
  cap?: string;
  proprietari?: ProprietarioVM[];
  struttura?: boolean;
}

export interface CastedParamsVM {
  codiceEnte: string;
  progressivoPratica: string;
  tipoRegistro: string;
  idAnnoPratica: string;
}

interface AccordionIssuedAssessmentFormProps {
  disabled?: boolean;
  values?: VerificationMessiVM;
  errors?: FormikErrors<VerificationMessiVM>;
  touched?: FormikTouched<VerificationMessiVM>;
  onChange: (
    key: keyof VerificationMessiVM,
    value: VerificationMessiVM[keyof VerificationMessiVM]
  ) => void;
  onChanges: (values: VerificationMessiVM) => void;
  isUnavailability?: boolean;
  datiMesso?: PracticeData;
}

const AccordionIssuedAssessmentFormComponent = ({
  values,
  onChange,
  onChanges,
  touched,
  errors,
  isUnavailability,
  disabled,
  datiMesso,
}: AccordionIssuedAssessmentFormProps): React.ReactElement => {
  const params = useParams();
  const castedParams = params as unknown as CastedParamsVM;

  const [modalPrintSuccess, setModalPrintSuccess] = useState<{
    open: boolean;
    data?: string;
  }>({
    open: false,
  });

  const [disabledButtonAddProprietario, setDisabledButtonAddProprietario] =
    useState<boolean>(false);

  const [openModalAddProprietario, setOpenModalAddProprietario] = useState<{
    open: boolean;
    proprietario?: ProprietarioVM;
  }>({ open: false });

  const [tableProps, setTableProps] = useState<Record<string, number>>({
    page: 0,
    rowsPerPage: 10,
  });

  const manualNotification = useHttpClient<Blob>(
    `${__PRACTICE_API_URL__}/accertamento-messi/stampa-notifica-manuale`,
    {
      options: {
        method: 'POST',
      },
    }
  );

  const [proprietari, getProprietari] = useHttpFetch<
    HttpResponse<ProprietariResponse>,
    ProprietariRes
  >(
    `${__PRACTICE_API_URL__}/proprietario/ritorno-accertamento/${castedParams.codiceEnte}/${castedParams.idAnnoPratica}/${castedParams.tipoRegistro}/${castedParams.progressivoPratica}`,
    {
      onBootstrap: true,
      options: {
        method: 'GET',
      },
      transformer: (response) => {
        const list = (response.data.payload.proprietari || []).map((proprietario) => {
          const proprietarioTransformed = {
            id: proprietario.key.idProprietario?.toString() || '0',
            fiscalCode: proprietario.codFiscale,
            surname: proprietario.cognome,
            name: proprietario.nome,
            residence: proprietario.comune
              ? `${proprietario.comune} ${
                  proprietario.provincia ? `(${proprietario.provincia})` : ''
                }`
              : '',
            state: proprietario.statoNotifica?.toString(),
            disabled: proprietario.statoNotifica === 'M',
            checked: proprietario.flagRichiestaNotifica === '1',
          };
          return {
            ...proprietarioTransformed,
            proprietarioBE: proprietario,
          };
        });

        return {
          list,
          struttura: response.data.payload.struttura,
        };
      },
    }
  );

  const [pdfModal, setPdfModal] = useState<Partial<ModalPdfViewerState>>({
    data: '',
    open: false,
    name: '',
  });

  const onChangePage = useEventCallback((page: number) => {
    setTableProps({
      page,
      rowsPerPage: tableProps.rowsPerPage,
    });
  });

  const onChangeRowsPerPage = useEventCallback((rowsPerPage: number) => {
    setTableProps({
      page: 0,
      rowsPerPage,
    });
  });

  const onChangeAcquisitionSystem = useEventCallback((value: string | undefined) => {
    onChange('outcome', value);
  });

  const onChangeStreetNumberList = useEventCallback((item: StreetNumberVM | null) => {
    onChanges({
      number: item?.id ? Number.parseInt(item.id?.toString(), 10) : undefined,
      cap: item?.cap || '',
      exponent: item?.exponent,
    });
  });

  const castedStruttura = useMemo(
    () => (proprietari.data as ProprietariRes)?.struttura,
    [proprietari.data]
  );

  const castedProprietari = useMemo(
    () => ((proprietari.data as ProprietariRes)?.list as ProprietarioVM[]) || [],
    [proprietari.data]
  );

  useEffect(() => {
    onChanges({
      proprietari: castedProprietari.filter((proprietario) => proprietario.disabled),
      struttura: castedStruttura,
    });
  }, [castedProprietari]);

  const feProprietari = useMemo(() => {
    return castedProprietari.map((proprietarioCasted) => {
      const disableCheckbox =
        !!values?.proprietari?.find(
          (proprietarioChecked) => proprietarioChecked.id === proprietarioCasted.id
        )?.disabled || proprietarioCasted.disabled;

      return {
        ...proprietarioCasted,
        requireNotification: (
          <Fragment>
            <FormControlLabel
              control={
                <Checkbox
                  id={`${proprietarioCasted.id}-checkbox`}
                  checked={
                    !!values?.proprietari?.find(
                      (proprietarioChecked) => proprietarioChecked.id === proprietarioCasted.id
                    )?.checked
                  }
                  disabled={disableCheckbox}
                  onChange={() => {
                    let slicedProprietari = values?.proprietari?.slice() || [];

                    if (
                      slicedProprietari.find(
                        (proprietarioCheck) =>
                          proprietarioCheck.id === proprietarioCasted.id?.toString()
                      )
                    ) {
                      slicedProprietari = slicedProprietari.filter(
                        (proprietarioCheck) =>
                          proprietarioCheck.id !== proprietarioCasted.id?.toString()
                      );
                    } else {
                      slicedProprietari.push({
                        ...proprietarioCasted,
                        checked: true,
                      });
                    }
                    onChange('proprietari', slicedProprietari);
                  }}
                  inputProps={{ 'aria-label': 'Richiesta Notifica' }}
                />
              }
              label=""
            />
            <PmiButton
              id={`${proprietarioCasted.id}-button`}
              variant="outlined"
              color="cdmBrandThird"
              onClick={() => {
                (
                  manualNotification({
                    responseType: 'blob',
                    data: {
                      codiceEnte: castedParams.codiceEnte,
                      tipoRegistro: castedParams.tipoRegistro,
                      idAnnoPratica: castedParams.idAnnoPratica,
                      progressivoPratica: castedParams.progressivoPratica,
                      tipoEsito: '1',
                      proprietari: [proprietarioCasted.proprietarioBE],
                    },
                  }) as ResponsePromise<Blob>
                ).then((response) => {
                  const pdfCertificate = response?.data;

                  if (pdfCertificate) {
                    const blob = new Blob([pdfCertificate], { type: 'application/pdf' });
                    blobToBase64(blob).then((base64) => {
                      setPdfModal({
                        data: base64.split(',')[1],
                        name: 'Notifica',
                        open: true,
                      });
                    });
                  }
                  toast.success('Operazione eseguita con successo');
                  let slicedProprietari = values?.proprietari?.slice() || [];
                  if (
                    slicedProprietari.find(
                      (proprietarioCheck) =>
                        proprietarioCheck.id === proprietarioCasted.id?.toString()
                    )
                  ) {
                    slicedProprietari = slicedProprietari.map((proprietarioCheck) =>
                      proprietarioCheck.id === proprietarioCasted.id
                        ? {
                            ...proprietarioCheck,
                            checked: true,
                            disabled: true,
                          }
                        : proprietarioCheck
                    );
                  } else {
                    slicedProprietari.push({
                      ...proprietarioCasted,
                      disabled: true,
                      checked: true,
                    });
                  }
                  onChange('proprietari', slicedProprietari);
                });
              }}
            >
              Notifica Manuale
            </PmiButton>
            <IconButton
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                setOpenModalAddProprietario({ open: true, proprietario: proprietarioCasted });
              }}
              aria-label="Modifica elemento"
              aria-haspopup="true"
              style={{
                padding: '4px',
              }}
              disabled={
                proprietarioCasted.proprietarioBE?.tipoProprietario !== 'M' || disableCheckbox
              }
            >
              <Edit />
            </IconButton>
          </Fragment>
        ),
      };
    });
  }, [castedProprietari, values?.proprietari, onChange, manualNotification]);

  const isNotButtonAddProprietari: boolean = useMemo(() => {
    return (
      values?.outcome !== '1' ||
      castedParams.tipoRegistro !== TypeRegister.Immigration ||
      Boolean(
        castedProprietari.filter((item) => item.proprietarioBE?.tipoProprietario === 'M')?.length
      )
    );
  }, [castedProprietari, values?.outcome, castedParams.tipoRegistro]);

  const onAddPress = useEventCallback(() => {
    setOpenModalAddProprietario({ open: true });
  });

  const onCloseConfirmModalAddProprietario = useEventCallback((isEdit?: boolean) => {
    setOpenModalAddProprietario({ open: false });
    setDisabledButtonAddProprietario(true);
    if (!isEdit) {
      getProprietari();
    }
  });

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={6}>
        <DatePicker
          label="Data accertamento"
          aria-label="Data accertamento"
          value={values?.date || null}
          onChange={(date) => {
            onChange('date', date);
          }}
          disabled={!!datiMesso?.timestampOperazione}
          renderInput={(renderParams) => (
            <PmiTextField
              id="effectiveDate"
              {...renderParams}
              fullWidth
              error={touched?.date && Boolean(errors?.date)}
              helperText={errors?.date}
            />
          )}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <AcquisitionSystemList
          label="Esito accertamento"
          onChange={onChangeAcquisitionSystem}
          value={values?.outcome || ''}
          typePractice={isUnavailability ? TypeDomainPractice.Tipo8 : TypeDomainPractice.Tipo7}
          error={touched?.outcome && Boolean(errors?.outcome)}
          disabled={!!datiMesso?.esito}
        />
      </Grid>
      <Grid item xs={12}>
        <PmiTextField
          id="noteDelMesso"
          label="Note del messo"
          aria-label="Note del messo"
          fullWidth
          multiline
          rows={5}
          onChange={(event) => {
            onChange('noteMesso', event.target.value);
          }}
          value={values?.noteMesso || ''}
          disabled
        />
      </Grid>
      {isUnavailability && values?.outcome === Outcome.CambioIndirizzo ? (
        <>
          <Grid item xs={12} md={6}>
            <AddressAutoComplete
              required
              selectedValue={values?.addressAuto || null}
              handleChange={(value) => {
                onChange('addressAuto', value);
              }}
              disabled={disabled}
              error={touched?.addressAuto && Boolean(errors?.addressAuto)}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <StreetNumberList
              label="Numero *"
              onChange={onChangeStreetNumberList}
              noEnded
              valueStreetNumber={
                values?.number
                  ? {
                      id: values?.number,
                      value: `${values?.number?.toString()}${transformExponent(
                        values?.exponent || ''
                      )}`,
                    }
                  : null
              }
              showExponent
              disabled={!values?.addressAuto?.code || disabled}
              circulationArea={values?.addressAuto?.code?.toString() || ''}
              error={touched?.number && Boolean(errors?.number)}
              helperText={errors?.number}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <PmiTextField
              label="C.A.P. *"
              aria-label="cap"
              id="cap"
              value={values?.cap || ''}
              onChange={(event) => {
                onChange('cap', event.target.value);
              }}
              fullWidth
              error={touched?.cap && Boolean(errors?.cap)}
              helperText={errors?.cap}
              disabled={disabled}
            />
          </Grid>
        </>
      ) : undefined}
      {isUnavailability && values?.outcome === Outcome.Altro ? (
        <>
          <Grid item xs={12}>
            <PmiTextField
              id="address"
              label="Altro *"
              aria-label="Altro"
              fullWidth
              inputProps={{
                maxLength: '250',
              }}
              onChange={(event) => {
                onChange('addressAuto', { description: event.target.value });
              }}
              value={values?.addressAuto?.description || ''}
              error={touched?.addressAuto && Boolean(errors?.addressAuto)}
              helperText={errors?.addressAuto}
            />
          </Grid>
        </>
      ) : undefined}
      <Grid item xs={12}>
        <PmiTextField
          id="note"
          label="Note"
          aria-label="Note"
          fullWidth
          multiline
          rows={7}
          inputProps={{
            maxLength: '240',
          }}
          onChange={(event) => {
            onChange('note', event.target.value);
          }}
          value={values?.note || ''}
          error={touched?.note && Boolean(errors?.note)}
          helperText={errors?.note}
        />
      </Grid>
      {isNotButtonAddProprietari ? undefined : (
        <Grid item xs={12} display="flex" justifyContent="flex-end">
          <span>
            <IconButton
              onClick={onAddPress}
              aria-label="Aggiungi la posizione per cui è richiesta la notifica"
              aria-haspopup="true"
              disabled={disabledButtonAddProprietario}
            >
              <AddCircleOutline />
            </IconButton>
            <Typography color="secondary" display="inline-block">
              Aggiungi la posizione per cui è richiesta la notifica
            </Typography>
          </span>
        </Grid>
      )}
      {values?.outcome === '1' && castedProprietari.length > 0 ? (
        <Grid item xs={12}>
          <PmiTitleDivider>Proprietari a cui notificare la residenza</PmiTitleDivider>
          <Table
            onSort={noop}
            head={headCells}
            orders={{}}
            hideButtons
            isNotLayoutFixed
            page={tableProps.page}
            rowsPerPage={tableProps.rowsPerPage}
            body={feProprietari}
            onVisibilityPress={noop}
            count={feProprietari.length || 0}
            onPageChange={onChangePage}
            onRowsPerPageChange={onChangeRowsPerPage}
            ariaLabel="Proprietari"
            noSort
          />
        </Grid>
      ) : undefined}
      {openModalAddProprietario.open ? (
        <ModalAddProprietario
          open={openModalAddProprietario.open}
          castedParams={castedParams}
          onClose={() => setOpenModalAddProprietario({ open: false })}
          onCloseConfirm={(isEdit) => onCloseConfirmModalAddProprietario(isEdit)}
          proprietario={openModalAddProprietario.proprietario}
        />
      ) : undefined}
      <ModalViewerPDF
        open={pdfModal.open || false}
        data={pdfModal.data || ''}
        name={pdfModal.name || ''}
        title={pdfModal.title || 'Notifica'}
        onClose={() => {
          setPdfModal({ data: '', open: false, name: '' });
        }}
      />
      {modalPrintSuccess.open ? (
        <ModalPrintSuccess
          open={modalPrintSuccess.open}
          onYes={() => {
            setPdfModal({ open: false });
          }}
          onClose={() => {
            setPdfModal({
              title: 'Notifica',
              name: 'Notifica',
              data: modalPrintSuccess.data || '',
              open: true,
            });
            setModalPrintSuccess({ open: false });
          }}
        />
      ) : undefined}
    </Grid>
  );
};

AccordionIssuedAssessmentFormComponent.displayName = 'Accordion return issued form component';

const AccordionIssuedAssessmentForm = memo(AccordionIssuedAssessmentFormComponent);

export default AccordionIssuedAssessmentForm;
